import { useEffect, useMemo, useState } from "react";
import { generatePDFSasToken, generateSasToken, getCitationFilePath } from "../api/endpoints/fileAPI";
import { useConversation } from "../contexts/conversationContext";
import { useDeviceType } from "./useDeviceType";

type UseCitationFromConversationProps = { tabIndex: number };

const PDF_DOWNLOAD_PATH = "/files/download?path=";

function useCitationFromConversation({ tabIndex }: UseCitationFromConversationProps) {
    const { activeCitation } = useConversation();

    const [filePath, setFilePath] = useState("");
    const [fileDownloadPath, setFileDownloadPath] = useState("");
    const [fileExtension, setFileExtension] = useState("");

    const { isDesktop } = useDeviceType();

    const downloadFileName = useMemo(() => {
        if (!activeCitation) return "";

        const name = activeCitation.citation_display_name;

        // Match "filename.ext[stuff]"
        const match = name.match(/^(.*?)(\.[^.]+)\[(\d+|[^\]]+)\]$/);

        if (match) {
            const base = match[1]; // filename without extension
            const ext = match[2]; // extension (e.g., .pdf, .xlsx)
            const bracket = `[${match[3]}]`; // bracket content

            if (tabIndex === 0) return `${base}${bracket}${ext}`; // For split source files, return with bracket
            if (tabIndex === 1) return `${base}${ext}`; // For full source files, return without bracket
        }

        return name; // fallback: return original if it doesn't match pattern
    }, [activeCitation, tabIndex]);

    useEffect(() => {
        if (!activeCitation) return setFilePath("");

        const getPath = async () => {
            let source = "";

            if (tabIndex === 0)
                source = activeCitation?.split_source_file_path; // Split source
            else source = activeCitation?.original_file_blob_path; // Full source

            const parts = source.split(".");
            const extension = parts[parts.length - 1].toLowerCase();
            setFileExtension(extension);

            if (extension !== "pdf") {
                const sasToken = await generateSasToken(source);
                return setFilePath(sasToken);
            }

            if (isDesktop) return setFilePath(getCitationFilePath(source)); // For PDF files, return the content path

            // Get PDF Sas token for mobile pdfs
            const pdfSasToken = await generatePDFSasToken(source, downloadFileName, true);
            setFilePath(pdfSasToken);
        };

        getPath();
    }, [activeCitation, tabIndex]);

    useEffect(() => {
        if (!activeCitation) return setFileDownloadPath("");

        const getDownloadPath = async () => {
            let source = "";

            if (tabIndex === 0)
                source = activeCitation?.split_source_file_path; // Split source
            else source = activeCitation?.original_file_blob_path; // Full source

            const parts = source.split(".");
            const extension = parts[parts.length - 1].toLowerCase();
            setFileExtension(extension);

            setFileDownloadPath(`${PDF_DOWNLOAD_PATH}${source}`); // Set the download path
        };

        getDownloadPath();
    }, [activeCitation, tabIndex]);

    return {
        filePath,
        fileDownloadPath,
        downloadFileName,
        fileExtension
    };
}

export default useCitationFromConversation;
